// vg-binding: P7.D-01
// vg-binding: P7.D-06
//
// apps/vendor/src/features/dashboard/api.ts
//
// TanStack-Query hooks wrapping the Wave 4 BE dashboard endpoints.
// Polling: KPIs refetch every 30s; production-queue
// refetches on the same cadence so the colour-tier chips don't go stale.

import { csrfFetch } from '../../lib/api/csrfFetch';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

export interface KpiMetric {
  id: string;
  label: string;
  value: number;
  unit: '%';
  data_source: 'live' | 'placeholder';
  direction: 'higher_better' | 'lower_better';
}

export interface CapacityMeter {
  capacity_global: number;
  current_load: number;
  utilisation_percent: number;
  tier: 'green' | 'yellow' | 'red' | 'unknown';
  data_source: 'live' | 'placeholder';
}

export interface DashboardKpisData {
  metrics: KpiMetric[];
  capacity: CapacityMeter;
  generated_at: number;
}

export interface ProductionQueueRow {
  item_id: string;
  order_id: string;
  product: string;
  qty: number;
  sla_deadline_ms: number;
  sla_percent_used: number;
  color_tier: 'green' | 'yellow' | 'orange' | 'red';
  status: string;
}

export interface ProductionQueueData {
  rows: ProductionQueueRow[];
  generated_at: number;
  data_source: 'live' | 'placeholder';
}

interface ApiOk<T> {
  ok: true;
  data: T;
}
interface ApiErr {
  ok: false;
  error: { code: string; message: string };
}

async function unwrap<T>(res: Response): Promise<T> {
  const body = (await res.json()) as ApiOk<T> | ApiErr;
  if (!body.ok) {
    const err = new Error(body.error?.message ?? 'Request failed') as Error & {
      code?: string;
    };
    err.code = body.error?.code;
    throw err;
  }
  return body.data;
}

const KPIS_KEY = ['vendor', 'dashboard', 'kpis'] as const;
const QUEUE_KEY = ['vendor', 'dashboard', 'production-queue'] as const;

const POLL_30S = 30 * 1000;

export function useDashboardKpis(): UseQueryResult<DashboardKpisData, Error> {
  return useQuery<DashboardKpisData, Error>({
    queryKey: KPIS_KEY,
    queryFn: async () => {
      const res = await csrfFetch('/api/v1/vendor/dashboard/kpis', {
        method: 'GET',
        credentials: 'include',
      });
      return unwrap<DashboardKpisData>(res);
    },
    refetchInterval: POLL_30S,
    staleTime: 5 * 60 * 1000, // server-side compute heavy → 5-min cache
  });
}

export function useProductionQueue(limit = 5): UseQueryResult<
  ProductionQueueData,
  Error
> {
  return useQuery<ProductionQueueData, Error>({
    queryKey: [...QUEUE_KEY, limit],
    queryFn: async () => {
      const res = await csrfFetch(
        `/api/v1/vendor/dashboard/production-queue?limit=${encodeURIComponent(String(limit))}`,
        { method: 'GET', credentials: 'include' },
      );
      return unwrap<ProductionQueueData>(res);
    },
    refetchInterval: POLL_30S,
  });
}
